import {
  take,
  takeEvery,
  takeLatest,
  put,
  select,
  all,
  call,
} from 'redux-saga/effects'
import { push } from 'connected-react-router'
import { compose, mergeLeft, omit } from 'ramda'

import { OBSOLETE, TEST } from 'utils/constants'
import { EDIT_FEATURE_NOTIFY } from 'utils/notifications'
import { CURRENT_URI } from 'constants/featureSnapshots'

import * as featureAction from 'actions/feature/features'
import {
  getPins,
  getSignals,
  getSubGroups,
  UPDATE_SELECTED_INPUT,
} from 'actions/feature/inputs'
import { getNotes } from 'actions/feature/notes'
import {
  FEATURE_SNAPSHOTS_RECEIVED,
  getFeatureSnapshots,
} from 'actions/feature/snapshots'
import { getHarnesses } from 'actions/feature/harnesses'
import { apiAction, deleteSystemNotification } from 'actions/application'

import {
  getCurrentFeature,
  getFeatureIsDirty,
  getValidationDescriptions,
} from 'selectors/feature/features'
import { getRawSnapshotFeature } from 'selectors/feature/snapshots'
import { getFeaturesRouteMatch } from 'selectors/router'
import {
  getFeatureMetadata,
  getFeatureModuleName,
  getFeatureType,
} from 'selectors/feature/featureMetadata'
import { getOrphanQueryUris } from 'selectors/feature/functionGraphs'
import {
  getSignalsWithTranslation,
  conditionTransformSelector,
} from 'selectors/feature/inputs'
import { harnessPinIdSetSelector } from 'selectors/feature/harnesses'

import { denormalizeFeature, normalizeFeature } from 'schemas/feature'
import {
  featureSchema,
  featureSaveSchema,
} from 'schemas/validation/feature/feature'
import { validate } from 'sagas/validation'

import { update, get } from 'services/features'

import { DEFAULT_ID } from 'constants/featureDefaults'
import { SET_CONDITION_FIELD } from 'actions/feature/conditions'
import { SET_IS_EDITING } from 'actions/navigator'
import { ADD_OUTPUT_NODE, SET_OUTPUT_FIELD } from 'actions/feature/outputs'
import { SET_FEATURE_FIELD } from 'actions/feature/featureMetadata'
import { ADD_FUNCTION } from 'actions/feature/functions'

const {
  UPDATE_FEATURE,
  UPDATE_FEATURE_STATUS,
  COMMIT_UPDATE_FEATURE_STATUS,
  FEATURE_SAVED,
  OBSOLETE_FEATURE_SAVED,
  FEATURE_SAVE_FAILED,
  REQUESTING_FEATURE_DATA,
  FEATURE_DATA_RECEIVED,
  FEATURE_DATA_REQUEST_FAILED,
  RESET_FEATURE,
  LOAD_FEATURE_DATA,
  SAVE_FEATURE,
  commitUpdateFeatureStatus,
  CREATED_FEATURE_REVISION,
} = featureAction

const getFeatureAction = (featureId) =>
  apiAction({
    asyncFn: get,
    args: { featureId },
    successActionType: FEATURE_DATA_RECEIVED,
    failureActionType: FEATURE_DATA_REQUEST_FAILED,
  })
const updateFeatureAction = (feature) =>
  apiAction({
    asyncFn: update,
    args: { feature },
    successActionType: FEATURE_SAVED,
    failureActionType: FEATURE_SAVE_FAILED,
  })

const obsoleteFeature = (feature) =>
  apiAction({
    asyncFn: update,
    args: { feature },
    successActionType: OBSOLETE_FEATURE_SAVED,
    failureActionType: FEATURE_SAVE_FAILED,
  })

export function* watchLoadFeatureData() {
  yield takeEvery(LOAD_FEATURE_DATA, loadFeatureData)
}

/** loadFeatureData
 * *******************************
 * fetch/set originalFeature
 * fetch/set snapshots
 * fetch/set notes
 *
 * inHistoryMode?
 *  set currentFeature = currentSnapshot
 * else
 *  set currentFeature = originalFeature
 */

function* loadFeatureData({ featureId, snapshotUri }) {
  const {
    getFeature: importedGetFeatureAction,
    setFeatureAsCurrentFeature,
    setSnapshotAsCurrentFeature,
  } = featureAction

  const showSnapshot = snapshotUri && snapshotUri !== CURRENT_URI

  yield all([
    put(importedGetFeatureAction(featureId)),
    put(getFeatureSnapshots(featureId)),
    put(getNotes(featureId)),
    put(getHarnesses()),
    // "take" makes it wait for the before triggered actions to finish
    take(FEATURE_DATA_RECEIVED),
    take(FEATURE_SNAPSHOTS_RECEIVED),
  ])

  if (showSnapshot) {
    const { feature } = yield select(getRawSnapshotFeature, {
      uri: snapshotUri,
    })
    const normalizedFeature = normalizeFeature(feature)
    yield put(setSnapshotAsCurrentFeature(normalizedFeature))
  } else {
    yield put(setFeatureAsCurrentFeature())
  }
}

export function* watchGetFeature() {
  yield takeEvery(REQUESTING_FEATURE_DATA, getFeature)
}
function* getFeature({ featureId }) {
  yield all([
    put(getFeatureAction(featureId)),
    put(getPins()),
    put(getSubGroups()),
  ])
  const {
    data: { feature },
  } = yield take(FEATURE_DATA_RECEIVED)
  const { moduleName } = Object.values(feature)[0]
  yield put(getSignals(moduleName))
}

export function* watchUpdateFeature() {
  yield takeEvery(UPDATE_FEATURE, updateFeature)
}

function* updateFeature() {
  const feature = yield select(getCurrentFeature)

  const { conditions } = feature
  const transformedConditions = yield select((state) =>
    conditionTransformSelector(state, { conditions })
  )
  const transformedFeature = { ...feature, conditions: transformedConditions }
  const {
    params: { featureId: currentFeatureId },
  } = yield select(getFeaturesRouteMatch)

  if (!transformedFeature.feature[DEFAULT_ID]) {
    yield transformUpdate(currentFeatureId, transformedFeature)
  }
}

const transformUpdate = (currentFeatureId, feature) =>
  compose(
    put,
    updateFeatureAction,
    omit(['status']),
    denormalizeFeature(currentFeatureId)
  )(feature)

export function* watchSaveFeature() {
  yield takeEvery(SAVE_FEATURE, testSaveFeature)
}

function* testSaveFeature(action) {
  const feature = yield select(getCurrentFeature)
  const descriptions = yield select(getValidationDescriptions)
  const {
    params: { featureId: currentFeatureId },
  } = yield select(getFeaturesRouteMatch)

  const denormalized = denormalizeFeature(currentFeatureId, feature)
  yield call(
    validate,
    featureSaveSchema,
    denormalized,
    null,
    { descriptions },
    function* validateCallback() {
      yield call(action.callback)
      yield put({ type: UPDATE_FEATURE })
    }
  )
}

export function* watchUpdateFeatureStatus() {
  yield takeLatest(UPDATE_FEATURE_STATUS, watchUpdateFeatureActiveStatus)
}

// need intermediate action for updating status
// this is the easiest way to get orphanQueryUris needed for updating the status to active
function* watchUpdateFeatureActiveStatus({ status, previousStatus }) {
  const orphanUris = yield select(getOrphanQueryUris)

  yield put(commitUpdateFeatureStatus(status, previousStatus, orphanUris))
}

export function* watchCommitUpdateFeatureStatus() {
  yield takeLatest(COMMIT_UPDATE_FEATURE_STATUS, updateFeatureStatus)
}

function* updateFeatureStatus({ status }) {
  const feature = yield select(getCurrentFeature)
  const {
    params: { featureId: currentFeatureId },
  } = yield select(getFeaturesRouteMatch)
  const { conditions } = feature
  const transformedConditions = yield select((state) =>
    conditionTransformSelector(state, { conditions })
  )
  const transformedFeature = { ...feature, conditions: transformedConditions }
  const denormalized = denormalizeFeature(currentFeatureId, transformedFeature)
  // only need to validate when sending to test
  if (status === TEST) {
    const descriptions = yield select(getValidationDescriptions)
    const orphanUris = yield select(getOrphanQueryUris)
    const signalsWithTranslations = yield select(getSignalsWithTranslation)
    const pinSet = yield select(harnessPinIdSetSelector)
    const featureModuleName = yield select(getFeatureModuleName)
    const featureType = yield select(getFeatureType)

    yield validate(
      featureSchema,
      denormalized,
      null,
      {
        descriptions,
        orphanUris,
        signalsWithTranslations,
        pinSet,
        featureModuleName,
        featureType,
      },
      () => transformStatusUpdate(status)(denormalized)
    )
  } else {
    yield transformStatusUpdate(status)(denormalized)
  }
}
const transformStatusUpdate = (status) =>
  compose(
    put,
    status === OBSOLETE ? obsoleteFeature : updateFeatureAction,
    mergeLeft({ status })
  )

export function* watchFeatureSaved() {
  yield takeEvery([FEATURE_SAVED, OBSOLETE_FEATURE_SAVED], featureSaved)
}
function* featureSaved() {
  const feature = yield select(getFeatureMetadata)

  if (feature.status === TEST) {
    // new snapshot is created, update snapshots in redux
    yield put(getFeatureSnapshots(feature.id))
  }
}

export function* watchResetFeature() {
  while (true) {
    yield take(RESET_FEATURE)
    yield put(deleteSystemNotification({ notificationId: EDIT_FEATURE_NOTIFY }))
  }
}

export function* watchUserChanges() {
  const isEditing = yield takeEvery(
    [SET_IS_EDITING],
    ({ isEditing }) => isEditing === true
  )
  if (isEditing)
    yield takeEvery(
      [
        SET_OUTPUT_FIELD,
        SET_CONDITION_FIELD,
        SET_FEATURE_FIELD,
        UPDATE_SELECTED_INPUT,
        ADD_FUNCTION,
        ADD_OUTPUT_NODE,
      ],
      featureChanges
    )
}

function* featureChanges() {
  const isDirty = yield select(getFeatureIsDirty)
  if (!isDirty) {
    yield put(featureAction.setFeatureIsDirty(true))
  }
}

export function* watchFeatureRevision() {
  yield takeEvery(CREATED_FEATURE_REVISION, featureRevisionCreated)
}
function* featureRevisionCreated({
  data: {
    feature: { feature },
  },
}) {
  const [id] = Object.keys(feature)
  yield put(push(`/features/${id}`))
}
